/**
 * The event creator's state machine — the half of the component that has no UI.
 *
 * Web, embed, and native all drive this reducer, so form behaviour that users
 * notice (an end that follows the start, an all-day toggle that keeps your dates)
 * is written and tested once instead of three times.
 */

import {
  type EventDraft,
  type EventFormatId,
  type EventPerson,
  type EventProblem,
  type EventRecurrence,
  type EventShowAs,
  type EventStatus,
  type EventTime,
  eventDate,
  eventDateTime,
  sanitizeDraft,
  DATE_PATTERN,
  DATE_TIME_PATTERN,
  timeClock,
  timeDate,
  validateEvent,
} from '@project/event-format';

import { MAX_DURATION_MINUTES } from './duration';

export interface EventCreatorState {
  readonly draft: EventDraft;
  /** The format the download/copy action targets. */
  readonly formatId: EventFormatId;
  /** Problems are computed always but shown only after a submit attempt. */
  readonly submitted: boolean;
  /**
   * The zone timed values are written in. Held on the state, not just the draft,
   * so an all-day round trip doesn't forget it — an all-day draft has no zone.
   */
  readonly timeZone?: string;
}

export type EventCreatorAction =
  | { type: 'set-title'; value: string }
  | { type: 'set-description'; value: string }
  | { type: 'set-location'; value: string }
  | { type: 'set-url'; value: string }
  | { type: 'set-start-date'; value: string }
  | { type: 'set-start-time'; value: string }
  | { type: 'set-end-date'; value: string }
  | { type: 'set-end-time'; value: string }
  /** How long the event lasts; the end moves to match, the start stays where it is. */
  | { type: 'set-duration'; minutes: number }
  | { type: 'set-time-zone'; value: string | undefined }
  | { type: 'set-all-day'; value: boolean }
  | { type: 'set-status'; value: EventStatus | undefined }
  | { type: 'add-attendee'; value: EventPerson }
  | { type: 'remove-attendee'; index: number }
  | { type: 'toggle-reminder'; minutes: number }
  /** Set or clear the alert at a position — `undefined` removes it and closes the gap. */
  | { type: 'set-reminder'; index: number; minutes: number | undefined }
  | { type: 'set-show-as'; value: EventShowAs | undefined }
  /** The end's own zone, for an event that lands somewhere else — a flight, mostly. */
  | { type: 'set-end-time-zone'; value: string | undefined }
  | { type: 'set-recurrence'; value: EventRecurrence | undefined }
  | { type: 'set-format'; value: EventFormatId }
  | { type: 'load'; value: EventDraft }
  | { type: 'submit' }
  | { type: 'reset'; defaults: EventCreatorDefaults };

/** Clock a timed event falls back to when the user turns off "all day". */
const DEFAULT_START_CLOCK = '09:00';
/** A caller's clock is data from outside, so it is checked before it reaches a draft. */
const CLOCK_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
const DEFAULT_DURATION_MINUTES = 60;

export interface EventCreatorDefaults {
  /** `YYYY-MM-DD` the blank form opens on — callers pass today; the package has no clock. */
  readonly date: string;
  /**
   * `HH:mm` the blank form opens on. Defaults to 09:00, which is right for a future date and
   * wrong for this afternoon — a caller that knows the time of day passes the next half hour.
   * The package still has no clock of its own.
   */
  readonly clock?: string;
  readonly timeZone?: string;
  readonly formatId?: EventFormatId;
}

export function emptyDraft(defaults: EventCreatorDefaults): EventDraft {
  const clock = CLOCK_PATTERN.test(defaults.clock ?? '') ? defaults.clock : DEFAULT_START_CLOCK;
  const start = eventDateTime(`${defaults.date}T${clock}`, defaults.timeZone);
  return {
    title: '',
    start,
    end: shiftMinutes(start, DEFAULT_DURATION_MINUTES),
  };
}

export function initialState(defaults: EventCreatorDefaults): EventCreatorState {
  return {
    draft: emptyDraft(defaults),
    formatId: defaults.formatId ?? 'ics',
    submitted: false,
    ...(defaults.timeZone === undefined ? {} : { timeZone: defaults.timeZone }),
  };
}

/** Empty/partial controls are legitimate drafts, but cannot participate in date arithmetic. */
function usableTime(time: EventTime): boolean {
  const value = time.kind === 'date' ? time.date : time.dateTime;
  const pattern = time.kind === 'date' ? DATE_PATTERN : DATE_TIME_PATTERN;
  return (
    pattern.test(value) && Number.isFinite(Date.parse(time.kind === 'date' ? value : `${value}Z`))
  );
}

function minutesOfDay(clock: string): number {
  const [hours = '0', minutes = '0'] = clock.split(':');
  return Number(hours) * 60 + Number(minutes);
}

function addDays(date: string, days: number): string {
  const [year = 0, month = 1, day = 1] = date.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10);
}

function daysBetween(from: string, to: string): number {
  const parse = (date: string): number => {
    const [year = 0, month = 1, day = 1] = date.split('-').map(Number);
    return Date.UTC(year, month - 1, day);
  };
  return Math.round((parse(to) - parse(from)) / 86_400_000);
}

/** Moves a time forward by whole minutes, rolling into the next day as needed. */
function shiftMinutes(time: EventTime, minutes: number): EventTime {
  if (time.kind === 'date') return eventDate(addDays(time.date, Math.round(minutes / 1440)));
  const total = minutesOfDay(timeClock(time) ?? '00:00:00') + minutes;
  const dayOffset = Math.floor(total / 1440);
  const withinDay = ((total % 1440) + 1440) % 1440;
  const clock = `${String(Math.floor(withinDay / 60)).padStart(2, '0')}:${String(withinDay % 60).padStart(2, '0')}`;
  return eventDateTime(`${addDays(timeDate(time), dayOffset)}T${clock}`, time.timeZone);
}

/** Minutes from start to end, or `null` when either side isn't a usable clock time. */
function durationMinutes(draft: EventDraft): number | null {
  const { start, end } = draft;
  if (end === undefined || start.kind !== end.kind || !usableTime(start) || !usableTime(end))
    return null;
  const days = daysBetween(timeDate(start), timeDate(end));
  if (start.kind === 'date') return days * 1440;
  const startClock = timeClock(start);
  const endClock = timeClock(end);
  if (startClock === null || endClock === null) return null;
  return days * 1440 + minutesOfDay(endClock) - minutesOfDay(startClock);
}

/**
 * How long the event lasts, as the duration field says it — `null` when there is no usable end.
 *
 * An all-day end is the last day of the event rather than the morning after it (the exclusive
 * DTEND is ics.ts's business), so a one-day event has no days *between* its ends and a duration
 * of one day. `set-duration` reads the same convention back, so the field round-trips.
 */
export function eventDurationMinutes(draft: EventDraft): number | null {
  const span = durationMinutes(draft);
  if (span === null) return null;
  return draft.start.kind === 'date' ? span + 1440 : span;
}

/** Applies a new start while dragging the end along, so the duration survives edits. */
function withStart(draft: EventDraft, start: EventTime): EventDraft {
  const duration = durationMinutes(draft);
  if (duration === null || !usableTime(start)) return { ...draft, start };
  // The shifted end inherits the *start's* zone, which is right until the end has one of its
  // own — a flight's arrival must not be dragged back into the departure's zone by nudging the
  // departure time.
  const shifted = shiftMinutes(start, duration);
  const endZone = draft.end?.kind === 'dateTime' ? draft.end.timeZone : undefined;
  const startZone = draft.start.kind === 'dateTime' ? draft.start.timeZone : undefined;
  return {
    ...draft,
    start,
    end: endZone === startZone ? shifted : withZone(shifted, endZone),
  };
}

function withDate(time: EventTime, date: string): EventTime {
  if (time.kind === 'date') return eventDate(date);
  return eventDateTime(`${date}T${(timeClock(time) ?? '00:00:00').slice(0, 5)}`, time.timeZone);
}

function withClock(time: EventTime, clock: string): EventTime {
  if (time.kind === 'date') return time;
  return eventDateTime(`${timeDate(time)}T${clock}`, time.timeZone);
}

function withZone(time: EventTime, timeZone: string | undefined): EventTime {
  if (time.kind === 'date') return time;
  return eventDateTime(time.dateTime, timeZone);
}

/** All-day ⇄ timed, keeping the dates the user already picked and the session's zone. */
function convertAllDay(draft: EventDraft, allDay: boolean, zone: string | undefined): EventDraft {
  if (allDay === (draft.start.kind === 'date')) return draft;
  if (allDay) {
    return {
      ...draft,
      start: eventDate(timeDate(draft.start)),
      ...(draft.end === undefined ? {} : { end: eventDate(timeDate(draft.end)) }),
    };
  }
  const start = eventDateTime(`${timeDate(draft.start)}T${DEFAULT_START_CLOCK}`, zone);
  return { ...draft, start, end: shiftMinutes(start, DEFAULT_DURATION_MINUTES) };
}

type OptionalField = 'description' | 'location' | 'url' | 'status' | 'showAs' | 'recurrence';

/** Drops a cleared optional field instead of storing `''` or `undefined` on the draft. */
function withOptional<K extends OptionalField>(
  draft: EventDraft,
  key: K,
  value: EventDraft[K] | undefined,
): EventDraft {
  if (value === undefined || value === '') {
    // Only optional keys reach here, so the copy is still a complete draft.
    const next: Record<string, unknown> = { ...draft };
    delete next[key];
    return next as unknown as EventDraft;
  }
  return { ...draft, [key]: value };
}

/**
 * Puts a draft from outside the form into the state: an embed host's prefill, an AI proposal, a
 * restored link. Two things happen here that a plain assignment would miss —
 *
 * - it's sanitized, because nobody at the keyboard typed it (see sanitize.ts);
 * - the session zone follows the draft, or the zone field would show one thing while the event
 *   carried another, and the next all-day round trip would apply the stale one. An all-day draft
 *   carries no zone, so it keeps whatever the session already had.
 *
 * Shared by the `load` action and the hook's initial draft, which are the same situation.
 */
export function withLoadedDraft(state: EventCreatorState, draft: EventDraft): EventCreatorState {
  const loaded = sanitizeDraft(draft);
  const timeZone = loaded.start.kind === 'dateTime' ? loaded.start.timeZone : state.timeZone;
  return {
    ...state,
    draft: loaded,
    submitted: false,
    ...(timeZone === undefined ? { timeZone: undefined } : { timeZone }),
  };
}

export function eventCreatorReducer(
  state: EventCreatorState,
  action: EventCreatorAction,
): EventCreatorState {
  const { draft } = state;
  switch (action.type) {
    case 'set-title':
      return { ...state, draft: { ...draft, title: action.value } };
    case 'set-description':
    case 'set-location':
    case 'set-url': {
      const key = action.type.slice('set-'.length) as 'description' | 'location' | 'url';
      return { ...state, draft: withOptional(draft, key, action.value) };
    }
    case 'set-start-date':
      return { ...state, draft: withStart(draft, withDate(draft.start, action.value)) };
    case 'set-start-time':
      return { ...state, draft: withStart(draft, withClock(draft.start, action.value)) };
    case 'set-end-date':
      return draft.end === undefined
        ? state
        : { ...state, draft: { ...draft, end: withDate(draft.end, action.value) } };
    case 'set-end-time':
      return draft.end === undefined
        ? state
        : { ...state, draft: { ...draft, end: withClock(draft.end, action.value) } };
    case 'set-duration': {
      // Actions arrive from three skins and, through the embed, from a host's own controls, so
      // the bounds are checked here rather than trusted from whatever parsed the text.
      if (!Number.isFinite(action.minutes) || !usableTime(draft.start)) return state;
      const requested = Math.round(action.minutes);
      if (requested <= 0 || requested > MAX_DURATION_MINUTES) return state;
      // All-day durations are inclusive days (see eventDurationMinutes), so "1 day" leaves the
      // end on the start's date rather than moving it to tomorrow.
      const offset =
        draft.start.kind === 'date' ? Math.round(requested / 1440) * 1440 - 1440 : requested;
      return { ...state, draft: { ...draft, end: shiftMinutes(draft.start, offset) } };
    }
    case 'set-time-zone': {
      // The end follows the start only while the two agree. Once someone has given the end a
      // zone of its own, changing the start's must not silently drag it back.
      const endZone = draft.end?.kind === 'dateTime' ? draft.end.timeZone : undefined;
      const startZone = draft.start.kind === 'dateTime' ? draft.start.timeZone : undefined;
      const endFollows = endZone === startZone;
      return {
        ...state,
        ...(action.value === undefined ? { timeZone: undefined } : { timeZone: action.value }),
        draft: {
          ...draft,
          start: withZone(draft.start, action.value),
          ...(draft.end === undefined || !endFollows
            ? {}
            : { end: withZone(draft.end, action.value) }),
        },
      };
    }
    case 'set-end-time-zone':
      return draft.end === undefined
        ? state
        : { ...state, draft: { ...draft, end: withZone(draft.end, action.value) } };
    case 'set-all-day':
      return { ...state, draft: convertAllDay(draft, action.value, state.timeZone) };
    case 'set-status':
      return { ...state, draft: withOptional(draft, 'status', action.value) };
    case 'add-attendee':
      return {
        ...state,
        draft: { ...draft, attendees: [...(draft.attendees ?? []), action.value] },
      };
    case 'remove-attendee':
      return {
        ...state,
        draft: {
          ...draft,
          attendees: (draft.attendees ?? []).filter((_, index) => index !== action.index),
        },
      };
    case 'toggle-reminder': {
      const current = draft.remindersMinutesBefore ?? [];
      const next = current.includes(action.minutes)
        ? current.filter((minutes) => minutes !== action.minutes)
        : [...current, action.minutes].sort((a, b) => a - b);
      return { ...state, draft: { ...draft, remindersMinutesBefore: next } };
    }
    case 'set-reminder': {
      const current = [...(draft.remindersMinutesBefore ?? [])];
      if (action.minutes === undefined) current.splice(action.index, 1);
      else current[action.index] = action.minutes;
      // Sorted and de-duplicated: two alerts at the same offset would generate two identical
      // VALARMs, and a calendar showing the same reminder twice looks like a bug in the file.
      const next = [...new Set(current.filter((minutes) => minutes !== undefined))].sort(
        (a, b) => a - b,
      );
      return {
        ...state,
        draft: { ...draft, ...(next.length === 0 ? {} : { remindersMinutesBefore: next }) },
      };
    }
    case 'set-show-as':
      return { ...state, draft: withOptional(draft, 'showAs', action.value) };
    case 'set-recurrence':
      return { ...state, draft: withOptional(draft, 'recurrence', action.value) };
    case 'set-format':
      return { ...state, formatId: action.value };
    case 'load': {
      // A loaded draft comes from outside the form — an embed host's prefill, an AI proposal,
      // a restored link — so it's sanitized before it can reach a render or a generator.
      const loaded = sanitizeDraft(action.value);
      // The session zone follows the draft, or the zone field would show one thing while the
      // event carried another — and the next all-day round trip would apply the stale one. An
      // all-day draft carries no zone, so it keeps whatever the session already had.
      const timeZone = loaded.start.kind === 'dateTime' ? loaded.start.timeZone : state.timeZone;
      return {
        ...state,
        draft: loaded,
        submitted: false,
        ...(timeZone === undefined ? { timeZone: undefined } : { timeZone }),
      };
    }
    case 'submit':
      return { ...state, submitted: true };
    case 'reset':
      return { ...initialState(action.defaults), formatId: state.formatId };
  }
}

/** Problems worth showing right now — nothing until the user tries to export. */
export function visibleProblems(state: EventCreatorState): EventProblem[] {
  return state.submitted ? validateEvent(state.draft) : [];
}

/** The problem to show under a field, if any. */
export function problemFor(
  problems: readonly EventProblem[],
  field: string,
): EventProblem | undefined {
  return problems.find((problem) => problem.field === field);
}
